import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import {
  makeStyles,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Chip,
  Avatar,
  Tooltip,
  Fade,
  useTheme,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";

// Icons
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import PeopleIcon from "@material-ui/icons/People";
import AdminPanelSettingsIcon from "@material-ui/icons/Security";
import PersonIcon from "@material-ui/icons/Person";
import EmailIcon from "@material-ui/icons/Email";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];
    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });
    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);
    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;
    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === "dark" ? "#0a0a0f" : "#f0f4f8",
    minHeight: "calc(100vh - 70px)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  // Header
  header: {
    marginBottom: theme.spacing(3),
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(59, 130, 246, 0.4)",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  // Add Button
  addButton: {
    background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    color: "#fff",
    borderRadius: 10,
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 4px 14px rgba(59, 130, 246, 0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
      boxShadow: "0 6px 20px rgba(59, 130, 246, 0.5)",
    },
  },
  // Search
  searchWrapper: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  searchField: {
    minWidth: 300,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper,
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
      },
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  // Stats
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(2, 3),
    borderRadius: 12,
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 150,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  // Table
  tableContainer: {
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  tableWrapper: {
    overflowX: "auto",
    maxHeight: "calc(100vh - 400px)",
    ...theme.scrollbarStyles,
  },
  table: {
    "& .MuiTableHead-root": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      "& .MuiTableCell-head": {
        fontWeight: 600,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        padding: theme.spacing(2),
        whiteSpace: "nowrap",
      },
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
      "& .MuiTableCell-body": {
        padding: theme.spacing(1.5, 2),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
      },
    },
  },
  // User Row
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  userAvatar: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    fontSize: "0.9rem",
    fontWeight: 700,
  },
  userName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "0.9rem",
  },
  userEmail: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  // Profile chips
  profileChip: {
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 28,
  },
  profileAdmin: {
    backgroundColor: alpha("#EF4444", 0.15),
    color: "#DC2626",
  },
  profileUser: {
    backgroundColor: alpha("#3B82F6", 0.15),
    color: "#2563EB",
  },
  // Action Buttons
  actionBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  editBtn: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },
  deleteBtn: {
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.2),
    },
  },
  // Empty state
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    background: alpha("#3B82F6", 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    color: "#3B82F6",
  },
}));

const Users = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);

  // Stats
  const totalUsers = users.length;
  const adminCount = users.filter((u) => u.profile === "admin").length;
  const userCount = users.filter((u) => u.profile === "user").length;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_USERS", payload: data.users });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-user`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Box className={classes.root}>
      {/* Modals */}
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        userId={selectedUser && selectedUser.id}
      />

      {/* Header */}
      <Fade in timeout={400}>
        <Box className={classes.header}>
          <Box className={classes.headerTop}>
            <Box className={classes.titleSection}>
              <Box className={classes.titleIcon}>
                <PeopleIcon />
              </Box>
              <Box>
                <Typography className={classes.title}>
                  {i18n.t("users.title")}
                </Typography>
                <Typography className={classes.subtitle}>
                  Gerencie os usuários e permissões
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              className={classes.addButton}
              startIcon={<AddIcon />}
              onClick={handleOpenUserModal}
            >
              {i18n.t("users.buttons.add")}
            </Button>
          </Box>

          {/* Search */}
          <Box className={classes.searchWrapper}>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              variant="outlined"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Stats */}
          <Box className={classes.statsRow} style={{ marginTop: 24 }}>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha("#3B82F6", 0.15) }}
              >
                <PeopleIcon style={{ color: "#3B82F6" }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{totalUsers}</Typography>
                <Typography className={classes.statLabel}>Total</Typography>
              </Box>
            </Box>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha("#EF4444", 0.15) }}
              >
                <AdminPanelSettingsIcon style={{ color: "#EF4444" }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{adminCount}</Typography>
                <Typography className={classes.statLabel}>Admins</Typography>
              </Box>
            </Box>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha("#22C55E", 0.15) }}
              >
                <PersonIcon style={{ color: "#22C55E" }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{userCount}</Typography>
                <Typography className={classes.statLabel}>Atendentes</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Table */}
      <Fade in timeout={600}>
        <Paper className={classes.tableContainer} elevation={0}>
          <Box className={classes.tableWrapper} onScroll={handleScroll}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{i18n.t("users.table.id")}</TableCell>
                  <TableCell>{i18n.t("users.table.name")}</TableCell>
                  <TableCell>{i18n.t("users.table.email")}</TableCell>
                  <TableCell align="center">{i18n.t("users.table.profile")}</TableCell>
                  <TableCell align="center">{i18n.t("users.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell align="center">
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        #{user.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box className={classes.userInfo}>
                        <Avatar className={classes.userAvatar}>
                          {getInitials(user.name)}
                        </Avatar>
                        <Typography className={classes.userName}>
                          {user.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box className={classes.userEmail}>
                        <EmailIcon fontSize="small" />
                        {user.email}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.profile === "admin" ? "Admin" : "Atendente"}
                        size="small"
                        className={`${classes.profileChip} ${
                          user.profile === "admin"
                            ? classes.profileAdmin
                            : classes.profileUser
                        }`}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            className={`${classes.actionBtn} ${classes.editBtn}`}
                            onClick={() => handleEditUser(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            className={`${classes.actionBtn} ${classes.deleteBtn}`}
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingUser(user);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>

            {!loading && users.length === 0 && (
              <Box className={classes.emptyState}>
                <Box className={classes.emptyIcon}>
                  <PeopleIcon style={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Nenhum usuário encontrado
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Adicione seu primeiro usuário
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default Users;
